import { UIKit } from "@/shared/ui";
/**
 * Виджет «Свойства» — правая панель редактора.
 *
 * Показывает параметры выделенного объекта (стена / проём / угол / прибор / светильник /
 * кабель) и позволяет задать их числом, а не мышью: для чертежа это основной способ ввода.
 * У выключателя здесь видно, какие светильники он зажигает, у светильника — какой
 * выключатель его включает: группа освещения читается из проводки, а не хранится.
 * Не хранит паспорт проекта и экспликацию: это отдельное меню верхней панели.
 */
import { wallLength } from "@/widgets/plan";
import {
	CATALOG,
	DoorMechanism,
	doorItemOf,
	windowItemById,
	type CatalogItem,
	type CatalogItemBase,
} from "@/shared/catalog";
import { useWallsStore } from "@/shared/store/layers";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { ObjectComments } from "./ObjectComments";

export type PropertiesPanelCatalogItem = CatalogItem;

export const OpeningProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const graph = layers.walls;
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const updateOpening = useWallsStore((s) => s.updateOpening);
	const pushHistory = useProjectStore((s) => s.pushHistory);

	// Хелпер для updateFixture в зависимости от каталога

	const onDelete = () => {
		// Удаление кнопкой отменяется по Ctrl+Z, как и клавишей Delete
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	if (!selection) return null;
	if (selection.kind === SelectionKind.Opening) {
		const opening = graph.openings[selection.id];
		if (!opening) return null;
		const wall = graph.walls[opening.wallId];
		const length = wall ? wallLength(graph, wall) : 0;
		const isWindow = opening.kind === "window";
		const sliding = !isWindow && doorItemOf(opening.item).mechanism === DoorMechanism.Sliding;
		const types = isWindow ? CATALOG.window.items : CATALOG.door.items;
		const typeOf = (id: string | undefined) => types.find((item) => item.id === id) ?? types[0];
		// Смена типа — свой шаг истории. Ширину проёма в стене сохраняем; у окна высоту и
		// подоконник берём из нового типа: балконная дверь и панорамное окно идут до пола
		const onPickType = (itemId: string) => {
			const windowType = windowItemById(itemId);
			useProjectStore
				.getState()
				.recordEdit(() =>
					updateOpening(
						opening.id,
						windowType
							? { item: itemId, height: windowType.height, sill: windowType.sill }
							: { item: itemId },
					),
				);
		};
		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					{isWindow ? "Окно" : "Дверь"}
				</UIKit.Text>
				<CatalogTypeList items={types} current={typeOf(opening.item).id} onPick={onPickType} />
				<UIKit.NumberField
					label="Ширина"
					value={opening.width}
					min={300}
					max={Math.max(300, length - 100)}
					onChange={(width) => updateOpening(opening.id, { width })}
				/>
				<UIKit.NumberField
					label="Высота"
					value={opening.height}
					min={300}
					max={wall ? wall.height : 3000}
					onChange={(height) => updateOpening(opening.id, { height })}
				/>
				<UIKit.NumberField
					label={isWindow ? "Высота подоконника" : "Порог"}
					value={opening.sill}
					min={0}
					max={wall ? wall.height - 300 : 2000}
					onChange={(sill) => updateOpening(opening.id, { sill })}
				/>
				{!isWindow && (
					<div className="flex gap-1.5">
						<UIKit.Button
							type="button"
							className="flex-1 rounded-lg border border-border bg-surface px-2 py-1.75 text-xs text-ink hover:border-slate-300 hover:text-ink"
							onClick={() => updateOpening(opening.id, { flipHinge: !opening.flipHinge })}
						>
							{sliding ? "Сдвиг ↔" : "Петли ↔"}
						</UIKit.Button>
						<UIKit.Button
							type="button"
							className="flex-1 rounded-lg border border-border bg-surface px-2 py-1.75 text-xs text-ink hover:border-slate-300 hover:text-ink"
							onClick={() => updateOpening(opening.id, { flipSwing: !opening.flipSwing })}
						>
							{sliding ? "Сторона ↕" : "Открывание ↕"}
						</UIKit.Button>
					</div>
				)}
				<ObjectComments kind="opening" id={opening.id} />
				<UIKit.Button
					type="button"
					className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-50"
					onClick={onDelete}
				>
					Удалить проём
				</UIKit.Button>
			</>
		);
	}

	return null;
};

/** Список типов раздела каталога — сменить тип объекта, не переставляя его. */
export function CatalogTypeList({
	items,
	current,
	onPick,
}: {
	items: readonly CatalogItemBase[];
	current: string;
	onPick: (itemId: string) => void;
}) {
	if (items.length === 0) return null;
	return (
		<>
			<UIKit.Text
				as="span"
				className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
			>
				Тип
			</UIKit.Text>
			<div className="flex flex-col gap-1">
				{items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						className={`flex items-center gap-2 rounded-lg border border-transparent px-2.25 py-1.75 text-left text-sm text-ink hover:bg-[#f3f5f9]${
							current === item.id ? " border-accent bg-accent-soft text-accent" : ""
						}`}
						onClick={() => onPick(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
		</>
	);
}
